/**
 * Prest0n Session Client
 *
 * HTTP client for the backend Prest0n Session Runtime API.
 * All calls are authenticated with the Firebase ID token.
 *
 * Base URL: https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net/
 *
 * Endpoints:
 *   POST /prest0nSessionCreate              → create a new session
 *   GET  /prest0nSessionGet?sessionId=...   → get session info
 *   POST /prest0nSessionTurn                → send a turn (streaming or not)
 *   POST /prest0nUpdateSessionContext       → update pageContext
 */

import { getAuth } from 'firebase/auth';
import { getActiveWorkspaceAccount } from '../activeWorkspace';
import { parseTurnRefusal, TurnRefusedError } from './turnRefusal';
import { replyRefForWire } from '../messageReplies/replyRefForWire';
import type { OrchestratorPageContext } from '../../config/pageContextRegistry';
import type { CardAttachment, CardAttachmentIssue } from '../../types/emailAttachmentCard';
import type {
  SessionInfo,
  TurnRequest,
  TurnResult,
  TurnStreamCallbacks,
  Prest0nTaskRecord,
  TaskStatus,
  TurnAttachment,
} from './types';
import { parseSSEStream } from './streamProcessor';
import {
  CLIENT_ONLY_TURN_PATIENCE_MS,
  selectTurnPatienceTimeoutMs,
} from './progress';

// ============================================================================
// Config
// ============================================================================

const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/**
 * Default per-request timeout for the non-streaming session-management calls
 * (create / get / context-update). Without this, a hung Cloud Function or a
 * slow network leaves `isLoading` true on the React side forever, which
 * silently wedges `handleSend()` (it early-returns when isLoading is true) —
 * the exact "no spinner, no response, prompt won't clear" symptom Fernando
 * reported during the 2026-05-13 IP-rollover incident.
 *
 * 25s is comfortably above the p99 cold-start latency for these endpoints
 * (~6–10s on a fresh GCF instance) but well below any user-perceptible "this
 * is permanently stuck" threshold.
 */
const SESSION_FETCH_TIMEOUT_MS = CLIENT_ONLY_TURN_PATIENCE_MS;

/** Race a fetch against a timeout so a hung request never wedges the hook. */
async function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit & { timeoutMs?: number } = {}
): Promise<Response> {
  const { timeoutMs = SESSION_FETCH_TIMEOUT_MS, signal: callerSignal, ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);

  // Forward an upstream abort onto our controller so callers can still cancel.
  const onAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onAbort);
  }
}

// ============================================================================
// Internal Helpers
// ============================================================================

/**
 * Get the current Firebase ID token for authenticated requests.
 * Throws if no user is authenticated.
 */
async function getFirebaseIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('[Prest0nSession] No authenticated user — cannot get ID token');
  }
  return user.getIdToken(false);
}

/**
 * Build standard auth headers for all API calls.
 */
async function buildAuthHeaders(): Promise<Record<string, string>> {
  const token = await getFirebaseIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  // PER-TAB ORGANIZATION (owner report 2026-09-11). Without this the server
  // resolved the acting workspace from users/{uid}/account_membership/main —
  // ONE value for the whole account — so a switch on another device sent this
  // tab's turns, billing and memory into the other organization.
  //
  // A HINT, never an authorization: the server verifies it against an active
  // member doc and falls back to the pointer when it cannot. Deliberately its
  // own header rather than the existing X-Account-Id, which is an UNVERIFIED
  // billing-attribution hint read as `|| 'unknown'` — overloading it would
  // make a forged value look like a decision.
  // Read the active workspace DIRECTLY rather than through knownAccountHint():
  // that helper exists to attribute BILLING (X-Account-Id, consumed unverified
  // as `|| 'unknown'`), and coupling an authorization input to it would let a
  // future change of its semantics quietly change who the server acts as.
  const workspace = (getActiveWorkspaceAccount() ?? '').trim();
  if (workspace) headers['X-Prest0n-Workspace'] = workspace;
  return headers;
}

/**
 * POST helper with auth headers.
 */
/**
 * The one throw for a non-OK response from any session endpoint.
 *
 * The diagnostic message is IDENTICAL to what it has always been — the console
 * line, orchestratorDecisionLog and every existing catch keep the full status
 * text. A recognised refusal is carried alongside it so the chat surface can
 * show the server's own sentence instead of the generic card; everything else
 * throws a plain Error exactly as before.
 */
function throwForFailedResponse(label: string, response: { status: number; statusText: string }, text: string): never {
  const diagnostic = `[Prest0nSession] ${label} failed: ${response.status} ${response.statusText} — ${text}`;
  const refusal = parseTurnRefusal(text);
  if (refusal) throw new TurnRefusedError(diagnostic, refusal);
  throw new Error(diagnostic);
}

async function authedPost<T>(endpoint: string, body: unknown): Promise<T> {
  const headers = await buildAuthHeaders();
  const response = await fetchWithTimeout(`${CF_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throwForFailedResponse(endpoint, response, text);
  }

  return response.json() as Promise<T>;
}

/**
 * GET helper with auth headers.
 */
async function authedGet<T>(endpoint: string, params: Record<string, string> = {}): Promise<T> {
  const headers = await buildAuthHeaders();
  const query = new URLSearchParams(params).toString();
  const url = `${CF_BASE_URL}/${endpoint}${query ? `?${query}` : ''}`;
  const response = await fetchWithTimeout(url, { method: 'GET', headers });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throwForFailedResponse(endpoint, response, text);
  }

  return response.json() as Promise<T>;
}

/**
 * Routine Threads (2026-07-31): the browser's IANA timezone, attached to
 * every run-turn body so the backend's routine tools can default
 * `schedule.timeZone` to where the user actually is. Detection failure (old
 * WebView, jsdom without full Intl) returns undefined and the field is simply
 * omitted — fully backward compatible.
 */
export function detectClientTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz ? tz : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build the attachment payload sent from frontend -> Cloud Function.
 * Keep inline data only while storageUrl is missing.
 */
export function toCloudFunctionAttachments(
  attachments?: TurnAttachment[]
): TurnAttachment[] | undefined {
  return attachments?.map((att) => {
    // When storageUrl OR gcsPath is present, strip inline dataUrl to save
    // request body size. Backend uses gcsPath for parallel extraction.
    if (att.storageUrl || att.gcsPath) {
      return { ...att, dataUrl: undefined };
    }
    return att;
  });
}

/**
 * INLINE-BASE64 SEND GUARD (2026-06 reliability overhaul).
 *
 * Async turns are persisted into a Firestore task doc, which has a hard
 * 1 MiB document limit. Attachments whose background upload never finished
 * (no storageUrl AND no gcsPath) survive `toCloudFunctionAttachments`
 * stripping with their inline base64 `dataUrl` intact — and an oversized
 * task-doc write surfaces as a confusing backend 500 mid-turn. 700k chars
 * leaves headroom below the 1 MiB cap for the rest of the doc (message,
 * context, stage events).
 */
export const MAX_INLINE_DATAURL_TURN_CHARS = 700_000;

/**
 * The largest file that can still ride inline, in bytes.
 *
 * Derived from MAX_INLINE_DATAURL_TURN_CHARS rather than chosen: base64
 * expands by 4/3, so a file above this size blows the whole-turn inline
 * budget *on its own*, before any sibling is counted. Reading it into a
 * dataUrl therefore cannot ever help the turn — the bytes are guaranteed to
 * be stripped or refused — while it costs ~1.33x the file's size in live
 * string memory, and a per-thread snapshot far past what localStorage holds.
 *
 * Measured cost of ignoring this (2026-09-09): fifty clips totalling 398 MB
 * were each read to base64 concurrently — roughly 530 MB of strings beside
 * the 398 MB of File buffers and fifty video preview elements — and the tab
 * died before the uploads could finish. Previews never needed it:
 * ChatAttachment renders from URL.createObjectURL(file).
 */
export const MAX_INLINE_ELIGIBLE_BYTES = Math.floor((MAX_INLINE_DATAURL_TURN_CHARS * 3) / 4);

/**
 * Whether a file is small enough that an inline copy could actually be used.
 * Above the ceiling the answer is no, and the read is skipped: the record
 * still carries name/type/size, and its storage upload supplies the locator.
 */
export function isInlineEligible(sizeBytes: number | undefined): boolean {
  return typeof sizeBytes === 'number' && sizeBytes >= 0 && sizeBytes <= MAX_INLINE_ELIGIBLE_BYTES;
}

/**
 * How many files one message may carry.
 *
 * The stated capability is batches of up to fifty documents, and the server
 * has been measured at exactly that: fifty supplier records ingested, swept
 * and answered correctly (QA trial 5). The composer, though, carried a bare
 * `const maxAttachments = 20` copied into three handlers — drop, paste and
 * picker — with nothing explaining the number. Attaching the fiftieth file
 * simply produced "You can only attach up to 20 files per message", so the
 * product contradicted its own promise at the last step before the upload
 * (found in the browser, 2026-08-25).
 *
 * Twenty was not protecting anything a byte limit does not protect better:
 * the real constraint is the 1 MiB task doc, and that is already bounded by
 * MAX_INLINE_DATAURL_TURN_CHARS above, which counts the bytes that actually
 * ride inline rather than the number of files. Fifty small uploads are
 * nothing to it; one oversized inline file still trips it.
 *
 * Defined once, here beside the limit that does the real work, because three
 * copies of a magic number is how they drift apart.
 */
export const MAX_ATTACHMENTS_PER_MESSAGE = 50;

/**
 * Combined base64 length the outgoing turn would actually carry inline.
 * Mirrors the `toCloudFunctionAttachments` stripping rule: a dataUrl only
 * counts while BOTH storageUrl and gcsPath are missing (otherwise it is
 * stripped before the request is built).
 */
export function sumInlineDataUrlChars(
  attachments?: Array<Pick<TurnAttachment, 'dataUrl' | 'storageUrl' | 'gcsPath'>>
): number {
  if (!attachments?.length) return 0;
  return attachments.reduce((sum, att) => {
    if (att.storageUrl || att.gcsPath) return sum;
    return sum + (att.dataUrl ? att.dataUrl.length : 0);
  }, 0);
}

/**
 * An attachment the outgoing turn can actually DELIVER: it carries either a
 * durable locator the backend can fetch, or an inline copy it can read.
 *
 * A record with neither is a NAME and nothing else. That is worse than a
 * refusal, because it is invisible on both ends: the backend cannot report a
 * file it never received, so the user sees Prest0n answer "I don't see any
 * files attached" about files they watched themselves attach.
 */
export function isDeliverableAttachment(
  att?: Pick<TurnAttachment, 'dataUrl' | 'storageUrl' | 'storagePath' | 'gcsPath'>
): boolean {
  return Boolean(att && (att.dataUrl || att.storageUrl || att.storagePath || att.gcsPath));
}

/** What the send path should do about the attachments it is holding. */
export type AttachmentDeliveryVerdict =
  | { kind: 'ok' }
  | { kind: 'none-delivered'; attachedCount: number }
  | { kind: 'some-dropped'; droppedNames: string[] };

/**
 * ATTACHMENT-DELIVERY GUARD (2026-09-09).
 *
 * `sumInlineDataUrlChars` above catches uploads that did not finish *while
 * still holding an inline copy* — it refuses when those inline bytes exceed
 * what a task doc can hold. It cannot see the failure that matters most for
 * large media: a video too big to inline has no dataUrl to count, so when its
 * upload does not finish the record carries NO payload at all, the inline sum
 * is zero, and the guard waves through a turn with nothing attached.
 *
 * That is exactly what happened to the 50-clip / 398 MB run on 2026-09-09:
 * the turn reached the backend with `attachments: 0`, no ingest job was ever
 * created, and Prest0n correctly answered that it could not see any clips.
 * The upload failing is a bad experience; sending anyway and blaming the
 * model for it is a bug.
 *
 * Counted from the files the USER picked (`message.attachments`), because
 * that is the one fact that survives a failed serialization — a dropped
 * record cannot report its own absence.
 */
export function judgeAttachmentDelivery(
  attachedNames: ReadonlyArray<string>,
  serialized: ReadonlyArray<Pick<TurnAttachment, 'name' | 'dataUrl' | 'storageUrl' | 'storagePath' | 'gcsPath'>> | undefined
): AttachmentDeliveryVerdict {
  if (!attachedNames.length) return { kind: 'ok' };

  const deliverable = (serialized || []).filter(isDeliverableAttachment);
  if (!deliverable.length) return { kind: 'none-delivered', attachedCount: attachedNames.length };

  // Match by name: the serialized records are built from the same File list.
  // Duplicated filenames are consumed one-for-one so two copies of the same
  // name cannot mask each other.
  const remaining = new Map<string, number>();
  for (const att of deliverable) {
    const key = String(att.name || '');
    remaining.set(key, (remaining.get(key) || 0) + 1);
  }
  const droppedNames: string[] = [];
  for (const name of attachedNames) {
    const left = remaining.get(name) || 0;
    if (left > 0) remaining.set(name, left - 1);
    else droppedNames.push(name);
  }

  return droppedNames.length ? { kind: 'some-dropped', droppedNames } : { kind: 'ok' };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Create a new Prest0n session for an account.
 * Returns the new SessionInfo including the sessionId.
 */
export async function createPrest0nSession(
  accountId: string,
  pageContext?: OrchestratorPageContext,
  conversationId?: string
): Promise<SessionInfo> {
  // Backend returns { success, account, session } — extract the session field
  const response = await authedPost<{ success?: boolean; session?: SessionInfo; sessionId?: string } & Partial<SessionInfo>>(
    'prest0nCreateSession',
    {
      accountId,
      pageContext,
      conversationId,
    }
  );
  // Support both unwrapped SessionInfo and wrapped { session: SessionInfo }
  const session: SessionInfo = (response as any).session ?? (response as any);
  return session;
}

/**
 * Get session info by sessionId.
 * Returns null if the session does not exist or is expired.
 */
export async function getPrest0nSession(sessionId: string): Promise<SessionInfo | null> {
  try {
    // Backend returns { success, session } — extract the session field
    const response = await authedGet<{ success?: boolean; session?: SessionInfo } & Partial<SessionInfo>>(
      'prest0nGetSession',
      { sessionId }
    );
    return (response as any).session ?? (response as any) as SessionInfo;
  } catch (err) {
    console.warn('[Prest0nSession] Session not found or expired:', sessionId, err);
    return null;
  }
}

/**
 * The deterministic session id for a thread — MUST mirror the server's
 * sessionManager.deterministicThreadSessionId (`sess_thr_<threadId>`).
 */
export function deterministicThreadSessionId(threadId: string): string {
  return `sess_thr_${threadId}`;
}

/**
 * Resolve the session for a thread — SYNTHESIZED, no network (session-
 * hardening S3, Miles 2026-07-12).
 *
 * Session ids are deterministic per thread and `prest0nRunTurn` already
 * resolves/creates the server doc from `threadId` on every turn, so the old
 * pre-flight get/create round-trips bought nothing and cost everything:
 * seconds of dead time before EVERY first send per thread per pageload (the
 * window the resume-race lived in), a status gate that never re-opened
 * (completed sessions are never flipped back to 'active', so the localStorage
 * id was discarded and re-created every pageload), and per-device divergence.
 * Computing the id makes every device derive the SAME session by construction
 * and removes the race window at its root (the S1 guards remain as
 * belt-and-suspenders).
 *
 * Kept async for call-site compatibility. The legacy name is retained because
 * external callers treat this as "give me the session for this thread".
 */
export async function resumeOrCreatePrest0nSession(
  accountId: string,
  threadId: string,
  pageContext?: OrchestratorPageContext
): Promise<SessionInfo> {
  const normalizedThreadId = String(threadId || '').trim();
  if (!normalizedThreadId) {
    throw new Error('[Prest0nSession] threadId is required for deterministic session resolution');
  }
  const nowIso = new Date().toISOString();
  return {
    sessionId: deterministicThreadSessionId(normalizedThreadId),
    accountId,
    status: 'active',
    createdAt: nowIso,
    lastActiveAt: nowIso,
    conversationId: normalizedThreadId,
    ...(pageContext ? { pageContext } : {}),
  };
}

/**
 * Ingest-on-upload (2026-06 reliability overhaul) — response shape of
 * POST /prest0nStartIngest (202 Accepted).
 */
export interface StartIngestResult {
  success: boolean;
  jobId: string;
  totalFiles: number;
  status: 'pending';
}

/**
 * Kick off server-side extraction (`prest0nStartIngest`) for attachments
 * that are already sitting in GCS via signed-URL uploads — i.e. as soon as
 * the uploads succeed, NOT when the turn fires. The backend writes progress
 * to `accounts/{accountId}/ingestJobs/{jobId}` (live progress UI) and the
 * returned jobId is echoed back on the turn request (`ingestJobId`) so the
 * turn reuses the upload-time extraction results.
 *
 * Failure here is non-fatal by design: callers log and proceed without a
 * jobId, and the backend falls back to inline extraction.
 */
export async function startCloudIngest(params: {
  sessionId: string;
  threadId?: string;
  attachments: Array<Pick<TurnAttachment, 'name' | 'type' | 'size'> & { gcsPath: string }>;
}): Promise<StartIngestResult> {
  return authedPost<StartIngestResult>('prest0nStartIngest', {
    sessionId: params.sessionId,
    threadId: params.threadId,
    // Only the fields the ingest contract needs — never inline dataUrl.
    attachments: params.attachments.map((att) => ({
      name: att.name,
      type: att.type,
      size: att.size,
      gcsPath: att.gcsPath,
    })),
  });
}

/**
 * PRE-FLIGHT STAMP (stamp-race fix, 2026-07-10): mint a task id CLIENT-SIDE,
 * in the exact shape the backend's generateTaskId produces
 * (`task_<base36ts>_<rand>` — the ts segment also feeds the server's
 * deterministicMessageAnchorForTask). Stamped onto the pending widget as
 * serverTaskId BEFORE the network call, then sent as body.clientTaskId so
 * the backend enqueues the task under this exact id. A refresh during the
 * request/202 window — previously a guaranteed "Interrupted" card even
 * though the server task lived — now re-attaches.
 */
export function mintClientTaskId(): string {
  const ts = Date.now().toString(36);
  let rand = '';
  try {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    rand = Array.from(bytes, (b) => (b % 36).toString(36)).join('');
  } catch {
    rand = Math.random().toString(36).slice(2, 9);
  }
  return `task_${ts}_${rand}`;
}

/**
 * Send a user turn to the backend and process the SSE stream via callbacks.
 *
 * This is the main method for migrated accounts. It:
 * 1. Sends the turn request with streaming=true
 * 2. Pipes the SSE response through parseSSEStream
 * 3. Calls the provided callbacks as events arrive
 *
 * Returns when the stream is complete (done or error event).
 */
export async function sendPrest0nTurn(
  sessionId: string,
  message: string,
  callbacks: TurnStreamCallbacks,
  options?: {
    attachments?: TurnAttachment[];
    pageContext?: OrchestratorPageContext;
    signal?: AbortSignal;
    documentContent?: string;
    responseMode?: 'stream' | 'async' | 'auto';
    threadId?: string;
    ingestJobId?: string;
    requestedWorkflow?: 'deep-research' | 'routines';
    clientTaskId?: string;
    /** Reply to a specific message (2026-09-15) — see TurnRequest.replyTo. */
    replyTo?: import('../../types/messageReply').MessageReplyRef | null;
  }
): Promise<void> {
  // Keep inline dataUrl ONLY when storageUrl is missing.
  // This guarantees the backend always receives at least one usable data field
  // per attachment during the frontend-upload race window.
  const lightAttachments = toCloudFunctionAttachments(options?.attachments);

  const responseMode = options?.responseMode || 'stream';
  const body: TurnRequest = {
    message,
    attachments: lightAttachments,
    pageContext: options?.pageContext,
    stream: responseMode === 'stream',
    responseMode,
    documentContent: options?.documentContent,
    threadId: options?.threadId,
    // Ingest-on-upload: lets the backend reuse the upload-time extraction
    // job instead of re-extracting inline on async turns.
    ingestJobId: options?.ingestJobId,
    requestedWorkflow: options?.requestedWorkflow,
    clientTaskId: options?.clientTaskId,
    // Routine Threads (2026-07-31): viewer timezone so routine schedules can
    // default to it. Additive; omitted when detection fails.
    clientTimeZone: detectClientTimeZone(),
    // Reply to a specific message (2026-09-15): bounded by the same parser the
    // server runs; `verified` is the server's to set, never sent.
    ...(options?.replyTo ? { replyTo: replyRefForWire(options.replyTo) } : {}),
  };

  const response = await fetchWithTimeout(`${CF_BASE_URL}/prest0nRunTurn?sessionId=${encodeURIComponent(sessionId)}`, {
    method: 'POST',
    // Through buildAuthHeaders, not an inline literal: this is the STREAMING
    // turn — the busiest call in the product — and an inline copy silently
    // missed X-Prest0n-Workspace, which would have left the one path that
    // matters most resolving from the account-wide pointer (2026-09-11).
    headers: { ...(await buildAuthHeaders()), Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal: options?.signal,
    timeoutMs: selectTurnPatienceTimeoutMs(responseMode),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throwForFailedResponse('Turn request', response, text);
  }
  // Delivery ladder (PR-2): a 2xx means the server HAS the turn — the one
  // moment every lane (stream, 202, sync) shares. Fired before the body is read.
  try {
    callbacks.onAccepted?.();
  } catch {
    /* a listener's throw must never break the turn */
  }

  if (response.status === 202 || responseMode !== 'stream') {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const queued = await response.json() as TurnResult;
      if (queued.taskId && queued.status) {
        callbacks.onTaskQueued?.(queued.taskId, queued.status);
        return;
      }
    }
  }

  if (!response.body) {
    throw new Error('[Prest0nSession] No response body for streaming turn');
  }

  // Parse the SSE stream and dispatch events to callbacks
  await parseSSEStream(response.body, callbacks, options?.signal);
}

/**
 * Send a turn in non-streaming mode. Returns the complete result.
 * Useful for programmatic / background calls.
 */
export async function sendPrest0nTurnSync(
  sessionId: string,
  message: string,
  options?: {
    attachments?: TurnAttachment[];
    pageContext?: OrchestratorPageContext;
    responseMode?: 'stream' | 'async' | 'auto';
    threadId?: string;
    requestedWorkflow?: 'deep-research' | 'routines';
  }
): Promise<TurnResult> {
  // Keep inline dataUrl only when storageUrl is missing (same rule as streaming path).
  const lightAttachments = toCloudFunctionAttachments(options?.attachments);

  const body: TurnRequest = {
    message,
    attachments: lightAttachments,
    pageContext: options?.pageContext,
    stream: false,
    responseMode: options?.responseMode || 'async',
    threadId: options?.threadId,
    requestedWorkflow: options?.requestedWorkflow,
    // Routine Threads (2026-07-31): same additive timezone hint as the
    // streaming path — the two run-turn bodies must not drift.
    clientTimeZone: detectClientTimeZone(),
  };

  return authedPost<TurnResult>(`prest0nRunTurn?sessionId=${encodeURIComponent(sessionId)}`, body);
}

/**
 * Discriminated task-status result (durability audit rank #8, 2026-07-13).
 * The legacy null-on-any-error shape made a fetch TypeError during Wi-Fi
 * re-association (laptop wake) indistinguishable from a genuine HTTP 404 —
 * three offline polls in ~5-8s after wake painted a false terminal
 * "not found / Regenerate" card over a live task and stripped its
 * serverTaskId. Callers must count ONLY 'not_found' toward miss streaks.
 */
export type Prest0nTaskStatusResult =
  | { kind: 'ok'; task: Prest0nTaskRecord }
  | { kind: 'not_found' }
  | { kind: 'network_error'; error: unknown };

/**
 * Mid-turn interjection (2026-08-16): attach a message to the RUNNING task
 * so the server folds it into the in-flight reply (or answers it in a
 * server-owned follow-up turn). Returns accepted:false on ANY miss —
 * terminal task, network trouble, anything — and the caller falls back to
 * the ordinary queued send, so a message is never dropped.
 */
export async function postPrest0nTaskInterjection(
  taskId: string,
  text: string,
  options?: {
    /**
     * Files sent WITH the note (2026-09-15): locator-only records — the same
     * `toCloudFunctionAttachments` projection a turn ships, minus anything
     * that still lacks a storage locator (an inline copy cannot ride a note).
     */
    attachments?: TurnAttachment[];
    /** The bubble's own id, so a retried request never attaches the note twice. */
    clientNoteId?: string;
    /**
     * §11 WS-3 (PR-E2, 2026-09-21 — ruling T-R14): a PARTICIPANT's note on
     * someone else's running turn names the room and the account the live card
     * carries, so the server's participant door can admit it on the room's own
     * facts (a participant of the thread + the lever admits the account + a live
     * task that names this thread). Both absent ⇒ the asker's own path, byte for
     * byte.
     */
    threadId?: string;
    accountId?: string;
  },
): Promise<{
  accepted: boolean;
  /**
   * Why a note was NOT attached (2026-09-15): 'terminal' = the turn is already
   * finishing, so the message is a NEW ask (sent now, never queued);
   * 'not_found' = no live task under that id (an older server on a streamed
   * turn); 'limit' / 'empty' = the server's own bounds; 'network' = the request
   * never got an answer; 'not_admitted' = the participant door refused (the
   * lever is off, the turn is not live on this thread, or the caller is not in
   * the room) — today's path follows. Absent when accepted.
   */
  reason?: 'terminal' | 'not_found' | 'limit' | 'empty' | 'parallel' | 'network' | 'not_admitted';
  lane?: 'loop' | 'pipeline' | 'status';
  verdict?: 'parallel_task';
  ack?: string;
}> {
  try {
    const attachments = (toCloudFunctionAttachments(options?.attachments) || [])
      .filter((att) => Boolean(att.storagePath || att.gcsPath || att.storageUrl))
      .map((att) => ({ ...att, dataUrl: undefined }));
    const response = await authedPost<{ success?: boolean; accepted?: boolean; reason?: string; lane?: string; verdict?: string; ack?: string }>(
      'prest0nInterjectTurn',
      {
        taskId,
        text,
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(options?.clientNoteId ? { clientNoteId: options.clientNoteId } : {}),
        ...(options?.threadId ? { threadId: options.threadId } : {}),
        ...(options?.accountId ? { accountId: options.accountId } : {}),
      },
    );
    const lane = (response as any)?.lane;
    const verdict = (response as any)?.verdict;
    // Dynamic acknowledgment (2026-08-21): server-authored, specific to this
    // message and the live work — the caller prefers it over static copy.
    // Lane 'status' means the ack IS the answer (a mid-run progress question
    // was answered directly; nothing was attached to the task).
    const ackRaw = (response as any)?.ack;
    const ack = typeof ackRaw === 'string' ? ackRaw.replace(/\s+/g, ' ').trim().slice(0, 240) : '';
    const reasonRaw = (response as any)?.reason;
    const reason = reasonRaw === 'terminal' || reasonRaw === 'not_found' || reasonRaw === 'limit'
      || reasonRaw === 'empty' || reasonRaw === 'parallel' || reasonRaw === 'not_admitted'
      ? reasonRaw
      : undefined;
    return {
      accepted: (response as any)?.accepted === true,
      ...(reason && (response as any)?.accepted !== true ? { reason } : {}),
      ...(lane === 'loop' || lane === 'pipeline' || lane === 'status' ? { lane } : {}),
      // In-thread parallelism slice 1 (2026-08-19): the server triaged this
      // message as INDEPENDENT NEW WORK — the caller may dispatch it as a
      // second concurrent turn instead of queueing it.
      ...(verdict === 'parallel_task' ? { verdict } : {}),
      ...(ack ? { ack } : {}),
    };
  } catch {
    return { accepted: false, reason: 'network' };
  }
}

export async function getPrest0nTaskStatusDetailed(taskId: string): Promise<Prest0nTaskStatusResult> {
  try {
    const response = await authedGet<{ success?: boolean; task?: Prest0nTaskRecord }>('prest0nGetTaskStatus', { taskId });
    const task = (response as any).task ?? null;
    return task ? { kind: 'ok', task } : { kind: 'not_found' };
  } catch (err) {
    // Offline is definitively a network condition, never a missing task.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { kind: 'network_error', error: err };
    }
    // A served HTTP 404 / explicit not-found is a genuine miss; everything
    // else (fetch TypeError, 5xx, timeout, CORS blip) is transport trouble.
    const msg = String((err as any)?.message || err || '');
    const status = (err as any)?.status ?? (err as any)?.statusCode;
    if (status === 404 || /\b404\b|not[ _-]?found/i.test(msg)) {
      // INCIDENT-20260820 (Fix 4): a scope-degraded 404 means the server
      // could not enumerate the user's workspaces — the miss is NOT evidence
      // the task is gone. Classify as network-shaped so the terminal-miss
      // streak (shouldTreatMissingTaskAsTerminal) does not advance and no
      // "reply could no longer be found" card paints over a healthy run.
      // (authedGet embeds the response body text in the error message.)
      if (/scopeDegraded/i.test(msg)) {
        return { kind: 'network_error', error: err };
      }
      return { kind: 'not_found' };
    }
    return { kind: 'network_error', error: err };
  }
}

export async function getPrest0nTaskStatus(taskId: string): Promise<Prest0nTaskRecord | null> {
  const result = await getPrest0nTaskStatusDetailed(taskId);
  if (result.kind === 'ok') return result.task;
  if (result.kind === 'network_error') {
    console.warn('[Prest0nSession] Task status unavailable (network):', taskId, result.error);
  } else {
    console.warn('[Prest0nSession] Task not found:', taskId);
  }
  return null;
}

/**
 * Stop-doesn't-stop fix (2026-07-31): ask the backend to cancel a live async
 * task when the user hits Stop, so the server stops burning the turn instead
 * of completing minutes later and painting an answer "out of nowhere".
 *
 * SERVER CONTRACT (fixed): POST /prest0nCancelTask with JSON { taskId } and
 * the standard Firebase bearer auth. 200 → { success: true, status } once the
 * task doc is terminal — status 'failed' (errorCode 'cancelled_by_user') on a
 * normal cancel, or 'completed' when the Stop raced natural completion.
 * 404 → { error: 'Task not found.' } for an unknown/already-swept id; 400 on
 * a missing taskId; 401/405 per the shared endpoint conventions (authedPost
 * throws on every non-2xx).
 *
 * Callers MUST swallow ALL failures (log-only, fire-and-forget): the
 * client-side abort already protects the UI, and the server sweeper remains
 * the backstop when the cancel request never lands.
 */
export async function cancelPrest0nTask(
  taskId: string
): Promise<{ success: boolean; status?: TaskStatus }> {
  return authedPost<{ success: boolean; status?: TaskStatus }>('prest0nCancelTask', { taskId });
}

/**
 * Outward-action authorize card (2026-08-18). 'peek' reads the held action's
 * preview for the card render; 'authorize'/'cancel' decide it — the server
 * claim is transactional (exactly-once under double clicks) and executes the
 * HELD params server-side. 409 → already decided/expired (authedPost throws;
 * callers surface the message on the card).
 */
export interface PendingActionPreview {
  /** Comma-separated; may name SEVERAL people since 2026-09-17. The card
   *  parses it with actionCardKit's `splitRecipients` — the client twin of
   *  the server's one parse — so what is shown is what will be sent. */
  to: string;
  cc: string;
  /** Present only when there is one; an absent bcc renders no line. */
  bcc?: string;
  /** The server clipped a recipient list for storage. The card says so rather
   *  than showing fewer recipients than the send will carry. */
  recipientsTruncated?: boolean;
  subject: string;
  /**
   * 'rbac' (P4, 2026-09-16): this hold is a TEAM OR ROLE change, not an email.
   * The governance fields below carry the card's facts — the server wrote them
   * (functions/src/prest0nSession/governanceHold.ts), never the model — and the
   * email fields above stay present but empty so every existing reader (the
   * ledger, the data attributes, the receipt) keeps its shape.
   */
  kind?: 'rbac';
  op?: string;
  /** One line, imperative: `create the role “Paralegal”`. Mirrors `subject`. */
  summary?: string;
  /** The facts beneath it: the person, the role, the permissions, the note. */
  lines?: string[];
  /** The asker's verified display label. */
  askedBy?: string;
  /** TRUE on the ASKER's own card while it waits for somebody else to approve. */
  awaitingApproval?: boolean;
  /** Display labels of the people asked — routing only, never their roles (R8). */
  approvers?: string[];
  /** The FULL email body since 2026-08-29 (one-tile directive) — field name
   *  kept for wire compatibility; 20k server safety cap, `bodyTruncated`
   *  marks the rare over-cap case. */
  bodyPreview: string;
  bodyTruncated?: boolean;
  /**
   * ATTACHMENTS (2026-09-17). `attachments` is what is ATTACHED — at most one
   * file, which is the provider's own ceiling — and `attachmentIssues` is
   * every other named file with the reason: carried as a link in the body, or
   * not going at all. Display rows only: a name, a type, a size, a source, and
   * never a storage path or a signed url.
   */
  attachments?: CardAttachment[];
  attachmentIssues?: CardAttachmentIssue[];
  /** The mailbox this goes out FROM (2026-08-25) — shown on the card so the
   *  sender is never a mystery. Absent when no verified identity is known. */
  from?: string | null;
}

export async function peekPendingAction(
  actionId: string
): Promise<{
  success: boolean;
  status: string;
  preview: PendingActionPreview | null;
  /** Stamped when the action already executed (auto-send, refresh) — lets
   *  the surface render the receipt from the doc alone. */
  confirmation?: {
    delivered: boolean; kind: string; messageId: string | null;
    reason: string | null; remediation: string;
  } | null;
  autoApproved?: boolean;
  /** Re-armed: the doc is pending again after a failed attempt — the same
   *  draft can be authorized once more. */
  retryable?: boolean;
  /** Batch approvals (2026-08-27): holds staged by one turn share a batchId;
   *  batchIndex is the 1-based row order on the batch card. */
  batchId?: string | null;
  batchIndex?: number | null;
  decidedAt?: string | null;
}> {
  return authedPost('prest0nAuthorizeAction', { actionId, decision: 'peek' });
}

/**
 * The VERIFIED fate of a held email (owner directive 2026-08-20, Miles's
 * repro). The card used to read `success !== false` off this call and stamp
 * "sent ✓" on the button — the shape of the HTTP round trip, not the fate of
 * the email — and every non-2xx (expired card, already decided, no
 * permission, unreachable) threw out of authedPost and left the user with a
 * button that silently reset itself.
 *
 * `delivered` is TRUE only when the email provider confirmed the send. Every
 * other case names its own `kind` and carries `reason` + `remediation` so the
 * receipt card and Prest0n can both say what happened and what is next.
 */
export type PendingActionOutcomeKind =
  // Server-side send verdicts (functions/src/prest0nSession/sendConfirmation.ts).
  | 'sent' | 'unconfirmed' | 'not-connected' | 'permission-denied'
  | 'invalid-recipient' | 'attachment-rejected' | 'rate-limited'
  | 'transport-error' | 'provider-rejected'
  // A setup problem on OUR side, before the provider was ever reached
  // (INCIDENT-20260824). The server has returned this since the card-send
  // fix; this union did not list it, so every client switch fell through to
  // the generic "the provider refused it" branch — which then told the model
  // to explain a `reason` the server deliberately withholds, and to offer a
  // retry that did not exist. Both fabrications came from this missing line.
  | 'internal-error'
  // Governance outcomes (P4, 2026-09-16): a held team/role change ran through
  // the same RBAC core the Team & Roles page runs, as whoever decided it.
  // 'rbac-refused' = the platform said no (its own words ride `reason`);
  // 'rbac-failed' = a problem on our side, reason withheld by the server.
  | 'rbac-done' | 'rbac-refused' | 'rbac-failed'
  // Decision-level outcomes resolved here.
  | 'cancelled' | 'expired' | 'already-decided' | 'not-found'
  | 'not-allowed' | 'unreachable'
  // A newer held draft replaced this card (2026-08-25). NOT the user's
  // cancel — the stale card's answer names the replacement, never blames.
  | 'superseded';

export interface PendingActionOutcome {
  /** The decision itself completed (whatever the email's fate). */
  ok: boolean;
  decision: 'authorize' | 'cancel';
  /** The held action this outcome belongs to. Carried on the outcome so the
   *  receipt can offer a retry that re-authorizes THIS draft. */
  actionId?: string;
  /** pending_actions status: executed | failed | cancelled | expired | … */
  status: string;
  /** TRUE only on the provider's own confirmation. Never inferred. */
  delivered: boolean;
  kind: PendingActionOutcomeKind;
  to?: string;
  cc?: string;
  /** Echoed so a receipt never omits a recipient the send carried. */
  bcc?: string;
  subject?: string;
  decidedAt?: string;
  messageId?: string | null;
  /** The provider's (or the gate's) own words for the refusal. */
  reason?: string | null;
  /** What to do about it next, in plain words. */
  remediation?: string | null;
  /**
   * The SAME held draft is still armed and can be sent again — the server
   * proved nothing left the building, so re-authorizing cannot double-send.
   * The receipt renders its retry on this and nothing else.
   */
  retryable?: boolean;
  /** The mailbox this goes (or went) out FROM — the cards' "from:" line. */
  from?: string | null;
  /**
   * To recipients that arrived as Cc (2026-09-17). The Outlook send tool takes
   * ONE `to_email` and the toolkit has no To array, so Miles's ruling was to
   * move the extras to Cc automatically — and the receipt says so, because
   * otherwise the sender finds out from the recipient's own copy. Absent on
   * every other send.
   */
  movedToCc?: string[];
  /** The standing auto-send teach authorized this, not a click. */
  autoApproved?: boolean;
  /** The decide core's own promotable statement for a confirmed send. */
  output?: string;
}

/**
 * Map a refused decision (409/404/403) onto an outcome kind. A 409 on an
 * action that was ALREADY decided is not "nothing happened" — the server
 * carries the prior stamp back, so a second click (second tab, second
 * device) reports what actually became of the email rather than a blank
 * "not sent".
 */
function decisionKindFromBody(httpStatus: number, body: any): PendingActionOutcomeKind {
  const status = String(body?.status || '').toLowerCase();
  if (status === 'expired' || /expired/i.test(String(body?.error || ''))) return 'expired';
  if (status === 'superseded') return 'superseded';
  if (httpStatus === 403) return 'not-allowed';
  if (httpStatus === 404 && status !== 'cancelled') return 'not-found';
  if (body?.delivered === true) return 'sent';
  if (status === 'cancelled') return 'cancelled';
  if (typeof body?.kind === 'string' && body.kind) return body.kind as PendingActionOutcomeKind;
  // A SERVER CRASH IS NOT A DECISION (INCIDENT-20260824 follow-up). A 5xx from
  // the authorize endpoint means the request died somewhere inside
  // permission -> claim -> execute -> stamp. The claim may have committed and
  // the send may have gone out before it threw, so the email's fate is
  // genuinely unknown. Falling through to 'already-decided' told the user a
  // decision had been made elsewhere and rendered a flat "not sent" — a guess,
  // in the direction that gets one email sent twice. 'unconfirmed' is the kind
  // the rest of the stack already treats correctly: never sent, never failed,
  // check the Sent folder before anything else goes out.
  if (httpStatus >= 500) return 'unconfirmed';
  if (status && status !== 'not-found') return 'already-decided';
  return httpStatus === 404 ? 'not-found' : 'already-decided';
}

/** What a person may change on a staged email. The server applies the same
 *  allowlist again — this is convenience, never the boundary. */
export interface StagedSendEditPatch {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  /**
   * ADD AND REMOVE FILES (2026-09-17). The DESIRED list of file NAMES — never a
   * path — which the server re-resolves against whoever is editing. A name
   * left out is a removal; an empty array takes every file off; omitting the
   * field entirely leaves the files alone, so editing the subject costs no
   * file work.
   */
  attachments?: string[];
}

export type StagedSendEditResult =
  | { ok: true; rev: number; preview: PendingActionPreview }
  | { ok: false; kind: 'conflict'; rev: number | null; preview: PendingActionPreview | null }
  | { ok: false; kind: 'forbidden'; message: string }
  | { ok: false; kind: 'gone'; status: string; preview: PendingActionPreview | null }
  | { ok: false; kind: 'error' };

/**
 * Change a staged email that has not been decided yet (2026-09-17).
 *
 * Like `decidePendingAction` this deliberately does NOT throw: a refusal, a
 * conflict and a draft that stopped being a draft are all OUTCOMES the card
 * renders, not exceptions to swallow. `rev` is the optimistic-concurrency
 * token the caller last saw; the server refuses a stale one and hands back the
 * current draft rather than letting it clobber a colleague's edit.
 */
export async function editPendingAction(
  actionId: string,
  patch: StagedSendEditPatch,
  rev: number | null,
): Promise<StagedSendEditResult> {
  try {
    const headers = await buildAuthHeaders();
    const response = await fetchWithTimeout(`${CF_BASE_URL}/prest0nEditPendingAction`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ actionId, patch, ...(rev === null ? {} : { rev }) }),
    });
    const body = await response.json().catch(() => ({} as any));
    if (response.ok && body?.success) {
      return { ok: true, rev: Number(body.rev || 0), preview: body.preview as PendingActionPreview };
    }
    if (response.status === 409) {
      return {
        ok: false, kind: 'conflict',
        rev: Number.isFinite(Number(body?.rev)) ? Number(body.rev) : null,
        preview: (body?.preview as PendingActionPreview) || null,
      };
    }
    if (response.status === 403) {
      // The server's own R7 wording — the card shows it verbatim and never
      // composes a second sentence of its own.
      return { ok: false, kind: 'forbidden', message: String(body?.error || '') };
    }
    if (response.status === 410 || response.status === 404) {
      return {
        ok: false, kind: 'gone',
        status: String(body?.status || 'gone'),
        preview: (body?.preview as PendingActionPreview) || null,
      };
    }
    return { ok: false, kind: 'error' };
  } catch {
    return { ok: false, kind: 'error' };
  }
}

export async function decidePendingAction(
  actionId: string,
  decision: 'authorize' | 'cancel'
): Promise<PendingActionOutcome> {
  // Deliberately NOT authedPost: a refused or failed decision is an OUTCOME
  // to render, not an exception to swallow. Every path below returns a
  // structured outcome the card can show and Prest0n can explain.
  try {
    const headers = await buildAuthHeaders();
    const response = await fetchWithTimeout(`${CF_BASE_URL}/prest0nAuthorizeAction`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ actionId, decision }),
    });
    const body = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      return {
        ok: false,
        decision,
        status: String(body?.status || 'refused'),
        delivered: body?.delivered === true,
        kind: decisionKindFromBody(response.status, body),
        to: typeof body?.to === 'string' ? body.to : undefined,
        subject: typeof body?.subject === 'string' ? body.subject : undefined,
        // "why:" is only ever the email provider's own words. A server-side
        // string (or an HTTP status line) is ours, so it never becomes the
        // receipt's reason — the class remediation speaks instead.
        reason: typeof body?.reason === 'string' && body.reason ? body.reason.slice(0, 300) : null,
        remediation: typeof body?.remediation === 'string' ? body.remediation : null,
        retryable: body?.retryable === true,
        from: typeof body?.from === 'string' && body.from ? body.from : null,
      };
    }
    return {
      ok: true,
      decision,
      status: String(body?.status || (decision === 'cancel' ? 'cancelled' : 'executed')),
      delivered: body?.delivered === true,
      kind: (body?.kind as PendingActionOutcomeKind)
        || (decision === 'cancel' ? 'cancelled' : body?.delivered === true ? 'sent' : 'unconfirmed'),
      to: typeof body?.to === 'string' ? body.to : undefined,
      cc: typeof body?.cc === 'string' ? body.cc : undefined,
      subject: typeof body?.subject === 'string' ? body.subject : undefined,
      decidedAt: typeof body?.decidedAt === 'string' ? body.decidedAt : undefined,
      messageId: typeof body?.messageId === 'string' ? body.messageId : null,
      reason: typeof body?.reason === 'string' ? body.reason : null,
      remediation: typeof body?.remediation === 'string' ? body.remediation : null,
      retryable: body?.retryable === true,
      from: typeof body?.from === 'string' && body.from ? body.from : null,
      // Addresses only, validated as strings — this reaches a receipt the
      // sender reads, and it names real people.
      ...(Array.isArray(body?.movedToCc) && body.movedToCc.length
        ? {
          movedToCc: (body.movedToCc as unknown[])
            .map((v) => String(v ?? '').trim()).filter(Boolean).slice(0, 20),
        }
        : {}),
      autoApproved: body?.autoApproved === true,
      output: typeof body?.output === 'string' ? body.output : undefined,
    };
  } catch (err: any) {
    // The decision never reached the server — so the email's fate is
    // UNKNOWN, not failed. The claim is transactional, so retrying is safe.
    return {
      ok: false,
      decision,
      status: 'unreachable',
      delivered: false,
      kind: 'unreachable',
      reason: String(err?.message || err).slice(0, 300),
    };
  }
}

export async function triggerPrest0nTask(taskId: string): Promise<TurnResult> {
  // Firestore onCreate trigger is the primary execution path now.
  // Keep this as a no-op compatibility shim so existing polling code can call it safely.
  return Promise.resolve({ handled: true, taskId, status: 'pending' });
}

/** A non-terminal task returned by the resume-discovery endpoint. */
export interface Prest0nActiveTask {
  taskId: string;
  threadId: string | null;
  status: 'pending' | 'running';
  createdAtMs?: number | null;
  updatedAtMs?: number | null;
}

/**
 * Phase 1.5 — resume DISCOVERY. Ask the server for THIS account's non-terminal
 * tasks so the client can re-attach every thread's in-flight turn on hydrate
 * (not just the active one) and light background-thread chips only for tasks
 * the server confirms are still running. Returns [] on any failure (discovery
 * is best-effort; the per-message serverTaskId resume path remains the primary
 * mechanism for the active thread).
 */
export async function listActivePrest0nTasks(): Promise<Prest0nActiveTask[]> {
  try {
    const response = await authedGet<{ success?: boolean; tasks?: Prest0nActiveTask[] }>('prest0nListActiveTasks');
    const tasks = (response as any)?.tasks;
    return Array.isArray(tasks) ? tasks : [];
  } catch (err) {
    console.warn('[Prest0nSession] active-task discovery unavailable:', err);
    return [];
  }
}

/**
 * Update the page context for an existing session.
 * Called when the user navigates to a different page.
 */
export async function updatePrest0nSessionContext(
  sessionId: string,
  pageContext: OrchestratorPageContext
): Promise<void> {
  try {
    await authedPost('prest0nUpdateSessionContext', { sessionId, pageContext });
  } catch (err) {
    // Non-fatal — page context update is best-effort
    console.warn('[Prest0nSession] Failed to update session page context:', err);
  }
}

/**
 * Convert SerializedAttachment[] to TurnAttachment[] for the API.
 */
export function toTurnAttachments(
  serialized: import('../../components/GlobalChatSystem/types').SerializedAttachment[]
): TurnAttachment[] {
  return serialized.map((s) => ({
    name: s.name,
    type: s.type,
    size: s.size,
    storageUrl: s.storageUrl,
    storagePath: s.storagePath,
    dataUrl: s.dataUrl,
    gcsPath: s.gcsPath,
  }));
}

// ============================================================================
// Billing API
// ============================================================================

export interface BillingStatus {
  accountId: string;
  plan: string;
  status: string;
  billingEnabled: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  creditsRemaining: number;
  monthlyTokensUsed: number;
  dailyTokensUsed: number;
  monthlyTokenLimit: number;
  dailyTokenLimit: number;
  isOverMonthlyLimit: boolean;
  isOverDailyLimit: boolean;
  canProceed: boolean;
}

/**
 * Get the billing status for the authenticated account.
 */
export async function getPrest0nBillingStatus(accountId: string): Promise<BillingStatus> {
  const response = await authedGet<{ success: boolean; billing: BillingStatus }>('prest0nBillingStatus');
  return response.billing;
}

/**
 * Create a Stripe checkout session for upgrading the plan.
 */
export async function createPrest0nCheckoutSession(
  plan: string,
  returnUrl?: string
): Promise<{ sessionId: string; url: string }> {
  return authedPost<{ sessionId: string; url: string }>('prest0nCreateCheckout', {
    plan,
    returnUrl: returnUrl || `${window.location.origin}/portal`,
  });
}

export interface ConnectedApp {
  appName: string;
  displayName: string;
  status: 'connected' | 'disconnected' | 'pending';
  connectedAt: string | null;
  connectionId: string | null;
}

/**
 * List connected apps (Gmail, Calendar, etc.) for the authenticated account.
 */
export async function getPrest0nConnections(): Promise<ConnectedApp[]> {
  const response = await authedGet<{ success: boolean; connections: ConnectedApp[] }>('prest0nListConnections');
  return response.connections;
}

/**
 * Sync connected-app capabilities with live Composio state NOW (the same
 * reconcile the Integrations page triggers after an OAuth callback). Called
 * fire-and-forget by the in-chat connect card the moment its poll observes an
 * ACTIVE connection, so "connected in the chat" and "the assistant can use it"
 * never drift apart (owner directive 2026-08-11: Prest0n must be able to pull
 * information the instant an application is connected).
 */
export async function reconcilePrest0nConnections(): Promise<void> {
  await authedPost<{ success: boolean }>('prest0nReconcileConnections', {});
}

/**
 * Initiate OAuth connection for an app (Gmail, Calendar, etc.).
 */
export async function connectPrest0nApp(
  appName: string,
  redirectUrl?: string
): Promise<{ redirectUrl: string; connectionId: string }> {
  return authedPost('prest0nConnectApp', {
    appName,
    redirectUrl: redirectUrl || `${window.location.origin}/portal/connections`,
  });
}
